/**
 * 25.08.2026 — НЕ УДАЛЯТЬ по требованию владельца: служебные пометки только superadmin, пациент не видит.
 */
import { useState } from 'react';
import { StickyNote, Trash2, X } from 'lucide-react';
import { trainingNotesApi, type TrainingNote, type TrainingNoteKind, type NotifierAnchorKind } from '@/api/trainingNotes';
import { Button } from '@/components/ui';

const KIND_LABELS: Record<TrainingNoteKind, string> = {
  coaching: 'Коучинг',
  unanswered: 'Не ответили (снимок)',
  should_reply: 'Как отвечать',
  suggest_kb: 'В KB (черновик)',
  other: 'Другое',
};

interface TrainingNotesPanelProps {
  tenantId: string;
  conversationId: string;
  selectedMessageId: string | null;
  notes: TrainingNote[];
  onClose: () => void;
  onChanged: () => void;
}

export function TrainingNotesPanel({
  tenantId,
  conversationId,
  selectedMessageId,
  notes,
  onClose,
  onChanged,
}: TrainingNotesPanelProps) {
  const [noteKind, setNoteKind] = useState<TrainingNoteKind>('coaching');
  const [body, setBody] = useState('');
  const [suggestedReply, setSuggestedReply] = useState('');
  const [kbHint, setKbHint] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [periodCount, setPeriodCount] = useState<number | null>(null);

  const handleSave = async () => {
    const text = body.trim();
    if (!text || !tenantId) return;
    setSaving(true);
    setError(null);
    try {
      const anchorKind: NotifierAnchorKind = selectedMessageId ? 'message' : 'conversation';
      await trainingNotesApi.create({
        tenant_id: tenantId,
        anchor_kind: anchorKind,
        anchor_id: selectedMessageId || conversationId,
        note_kind: noteKind,
        body: text,
        suggested_reply: suggestedReply.trim() || null,
        kb_hint: kbHint.trim() || null,
        observed_at: noteKind === 'unanswered' ? new Date().toISOString() : null,
      });
      setBody('');
      setSuggestedReply('');
      setKbHint('');
      onChanged();
    } catch {
      setError('Не удалось сохранить пометку');
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside
      className="w-80 shrink-0 border-l border-amber-200 bg-amber-50/80 p-3 overflow-y-auto"
      data-testid="training-notes-panel"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-amber-800">
          <StickyNote className="w-4 h-4" />
          <h3 className="text-xs font-semibold uppercase tracking-wide">Служебные пометки</h3>
        </div>
        <button type="button" onClick={onClose} className="text-amber-700 hover:text-amber-900" aria-label="Закрыть">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[11px] text-amber-800/80 mb-3">
        Сырьё для обучения. Пациент и Public API не видят. Не SLA и не CIE.
      </p>
      <p className="text-[11px] text-shell-muted mb-2">
        {selectedMessageId ? `Якорь: сообщение ${selectedMessageId.slice(0, 8)}…` : 'Якорь: вся переписка'}
      </p>
      <label className="block text-[11px] text-shell-muted mb-2">
        Тип
        <select
          className="mt-1 w-full rounded-lg border border-amber-200 bg-white px-2 py-1.5 text-sm"
          value={noteKind}
          onChange={(e) => setNoteKind(e.target.value as TrainingNoteKind)}
          data-testid="training-note-kind"
        >
          {Object.entries(KIND_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </label>
      <textarea
        className="w-full rounded-lg border border-amber-200 bg-white px-2 py-1.5 text-sm min-h-[72px] mb-2"
        placeholder="На этот входящий по состоянию на T никто не ответил…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        data-testid="training-note-body"
      />
      <textarea
        className="w-full rounded-lg border border-amber-200 bg-white px-2 py-1.5 text-sm min-h-[48px] mb-2"
        placeholder="Черновик ответа (не в канал)"
        value={suggestedReply}
        onChange={(e) => setSuggestedReply(e.target.value)}
      />
      {noteKind === 'suggest_kb' && (
        <input
          className="w-full rounded-lg border border-amber-200 bg-white px-2 py-1.5 text-sm mb-2"
          placeholder="KB hint, без авто-upsert"
          value={kbHint}
          onChange={(e) => setKbHint(e.target.value)}
        />
      )}
      <Button size="sm" onClick={() => void handleSave()} disabled={saving || !body.trim()} testId="training-note-save">
        Сохранить
      </Button>
      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      <div className="mt-3 space-y-2">
        {notes.length === 0 ? (
          <p className="text-xs text-shell-muted">Пометок в этом чате нет.</p>
        ) : (
          notes.map((note) => (
            <article key={note.id} className="rounded-lg bg-white border border-amber-100 p-2 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-amber-700">
                    {KIND_LABELS[note.note_kind] || note.note_kind}
                    {note.anchor_kind === 'message' ? ' · сообщение' : ' · беседа'}
                  </p>
                  <p className="text-shell-text whitespace-pre-wrap">{note.body}</p>
                </div>
                <button
                  type="button"
                  className="text-shell-muted hover:text-red-600"
                  onClick={() => void trainingNotesApi.remove(note.id).then(onChanged)}
                  aria-label="Удалить пометку"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </article>
          ))
        )}
      </div>

      <div className="mt-3 pt-2 border-t border-amber-100 space-y-2">
        <p className="text-[10px] font-semibold text-shell-muted uppercase">Мои пометки за период</p>
        <div className="flex gap-1">
          <input type="date" className="flex-1 rounded border border-amber-200 px-1 py-1 text-[11px]" value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className="flex-1 rounded border border-amber-200 px-1 py-1 text-[11px]" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            void trainingNotesApi.listMine(from || undefined, to || undefined).then((items) => setPeriodCount(items.length));
          }}
        >
          Показать
        </Button>
        {periodCount != null && <p className="text-[11px] text-shell-muted">{periodCount} пометок</p>}
      </div>
    </aside>
  );
}
